import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { adminApiService } from '../services/admin.service';

/**
 * Contexto de autenticación para administradores (JWT based)
 * Proporciona:
 * - Almacenamiento seguro de JWT token en sessionStorage
 * - Login con email y password contra POST /admin/login
 * - Hook useAdminAuth() que retorna estado y métodos
 * - Manejo de sesiones (login, logout, error)
 */

export interface AdminAuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: { email: string; name: string; role: string } | null;
  error: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

const ADMIN_TOKEN_STORAGE_KEY = 'kubiy_admin_token';
const ADMIN_USER_STORAGE_KEY = 'kubiy_admin_user';

/**
 * AdminAuthProvider
 * Envuelve la aplicación y proporciona autenticación de admin
 */
export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<{ email: string; name: string; role: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Restaurar token y usuario desde sessionStorage al montar
  useEffect(() => {
    const storedToken = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    const storedUser = sessionStorage.getItem(ADMIN_USER_STORAGE_KEY);
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  /**
   * Logout: borra el token y usuario de sessionStorage
   */
  const logout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ADMIN_USER_STORAGE_KEY);
    setToken(null);
    setUser(null);
    setError(null);
  }, []);

  /**
   * Login: autentica con email y password contra POST /admin/login
   */
  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const ADMIN_API = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
        const response = await fetch(
          `${ADMIN_API}/admin/login`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          }
        );

        if (response.status === 401) {
          throw new Error('Email o contraseña inválida');
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || `Error al ingresar: HTTP ${response.status}`);
        }

        const data = await response.json();
        const { token, user: userData } = data.data;

        sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
        sessionStorage.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify(userData));
        setToken(token);
        setUser(userData);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
        setToken(null);
        setUser(null);
        sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(ADMIN_USER_STORAGE_KEY);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Configurar el token getter en ambas instancias del API service
  useEffect(() => {
    adminApiService.setTokenGetter(() => token || null);
    adminApiService.setLogoutCallback(() => logout());
  }, [token, logout]);

  const isAuthenticated = !!token;

  const value: AdminAuthContextType = {
    isAuthenticated,
    token,
    user,
    error,
    isLoading,
    login,
    logout,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

/**
 * Hook para usar el contexto de autenticación de admin
 * Lanza error si se usa fuera de AdminAuthProvider
 */
export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error(
      'useAdminAuth must be used within an AdminAuthProvider'
    );
  }
  return context;
};
